import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${canonical}`);
      
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
      updateMetaTag('twitter:url', `${siteUrl}${canonical}`);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema when component unmounts
      if (schema) {
        const scriptTag = document.querySelector('script[type="application/ld+json"]');
        if (scriptTag) {
          scriptTag.remove();
        }
      }
    };
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);
};

// Helper function to generate LocalBusiness schema
export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@type': 'Store',
    '@id': `${siteUrl}/#store`,
    name: 'Tienda La Bonita Market',
    description: 'Tienda de productos mexicanos, hondureños, salvadoreños y nicaragüenses en Indianápolis. Ofrecemos abarrotes, dulces, salsas y envíos internacionales con Paquetería Hernández.',
    url: siteUrl,
    telephone: '(463) 253-7180',
    email: 'labonitamarkett@gmail.com',
    image: 'https://public.readdy.ai/ai/img_res/75650fcad725dd7f256cd46c3cfa8cf1.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4051 Carson Ave',
      addressLocality: 'Indianapolis',
      addressRegion: 'IN',
      postalCode: '46227',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 39.7234567,
      longitude: -86.1456789,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      opens: '08:00',
      closes: '22:30',
    },
    priceRange: '$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Cash, Credit Card, Debit Card',
    servesCuisine: ['Mexican', 'Honduran', 'Salvadoran', 'Nicaraguan'],
    areaServed: {
      '@type': 'City',
      name: 'Indianapolis',
    },
    hasMap: 'https://www.google.com/maps/place/4051+Carson+Ave,+Indianapolis,+IN+46227',
    sameAs: [
      'https://www.instagram.com/labonitamarkett/',
      'https://www.tiktok.com/@labonitamarket',
    ],
  };
};

// Helper function to generate WebSite schema
export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'Tienda La Bonita Market',
    description: 'Tienda de productos mexicanos y centroamericanos en Indianápolis',
    inLanguage: 'es-US',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/?s={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

// Helper function to generate Organization schema
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Tienda La Bonita Market',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/44113e0bd02cf9d4b6e5b2a3bf2fb8ec/cba9e6b13988b672a24d545dc833d6a1.png',
      width: 512,
      height: 512,
    },
    image: 'https://readdy.ai/api/search-image?query=Vibrant%20Mexican%20grocery%20store%20exterior%20with%20colorful%20signage%2C%20traditional%20Latin%20American%20market%20facade%2C%20welcoming%20entrance%2C%20professional%20commercial%20photography&width=1200&height=630&seq=org-image-001&orientation=landscape',
    description: 'Tienda de productos mexicanos y centroamericanos en Indianápolis con envíos internacionales',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4051 Carson Ave',
      addressLocality: 'Indianapolis',
      addressRegion: 'IN',
      postalCode: '46227',
      addressCountry: 'US',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '(463) 253-7180',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: ['Spanish', 'English'],
      email: 'labonitamarkett@gmail.com',
    },
    sameAs: [
      'https://www.instagram.com/labonitamarkett/',
      'https://www.tiktok.com/@labonitamarket',
    ],
  };
};

// Helper function to generate FAQPage schema
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${siteUrl}/#faq`,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Helper function to generate BreadcrumbList schema
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
};
